// GenAIPrompts.tsx

const AVAILABLE_LANGUAGES = ["English", "Spanish", "Italian", "French", "German", "Portuguese"];

export function buildGenAiPayload(sourceLang: string, words: string[]) {
  const targetLangs = AVAILABLE_LANGUAGES.filter((lang) => lang !== sourceLang);

  // --- System Prompt with Enhanced Clarity on Source Options Language ---
  const systemContent = `
You are an AI assistant specialized in creating JSON data for vocabulary learning applications.
Your task is to process a list of words in a given **Source Language** and generate a JSON array.
For each word provided by the user:

1.  **SourceWord Object:**
    * Identify the word as 'Correct'. This word is in the **Source Language**.
    * Generate 3 **incorrect** but plausible distractors. These distractors MUST ALSO BE valid words in the **Source Language**.
    * Combine the 'Correct' word and the 3 distractors into the 'Options' array for 'SourceWord'.

2.  **Translations Object:**
    * Translate the source word into each of the specified 'Target Languages'.
    * For each translation (which is in a **Target Language**):
        * Identify it as 'Correct'.
        * Generate 3 **incorrect** but plausible distractors. These distractors MUST be valid words in that specific **Target Language**.
        * Combine the 'Correct' translation and its 3 distractors into the 'Options' array for that target language.

**CRITICAL CAPITALIZATION RULES:**
-   ALL translations must follow proper capitalization:
-   Capitalize ONLY the first letter of the entire translation (sentence case)
-   Do NOT capitalize every word - only the first letter of the phrase
-   Examples:
    * "good morning" → "Good morning"
    * "friend" → "Friend"
    * "how are you" → "How are you"
    * "good afternoon" → "Good afternoon"
    * "thank you very much" → "Thank you very much"
-   This capitalization rule applies to ALL languages in translations
-   Options/distractors should also follow the same capitalization pattern (first letter only)

**CRITICAL PLURAL CLARIFICATION RULES:**
When translating second person plural forms to English (since English uses "you" for both singular and plural):
-   If the source is second person plural (e.g., Spanish "ustedes", French "vous" (plural), German "ihr")
-   And the English translation would be "you" + verb
-   Then ADD a plural clarification in parentheses: "You walk (plural)"
-   Examples:
    * Spanish "ustedes caminan" → English "You walk (plural)"
    * Spanish "ustedes están" → English "You are (plural)"
    * French "vous marchez" (plural) → English "You walk (plural)"
    * German "ihr geht" → English "You go (plural)"
    * Italian "voi mangiate" → English "You eat (plural)"
-   For the distractors, maintain consistency with plural clarifications when appropriate

**CRITICAL GENDER CLARIFICATION RULES:**
When translating gendered words (especially from languages with grammatical gender like Spanish, French, German, Italian, Portuguese) to languages without gender distinctions (like English):
-   If the source word is gender-specific (e.g., Spanish "amiga" = female friend, "amigo" = male friend)
-   And the target language uses the same word for both genders (e.g., English "friend")
-   Then ADD a gender clarification in parentheses: "Friend (female)" or "Friend (male)" (lowercase gender marker)
-   This applies to professions, relationships, and any gendered nouns
-   Examples:
    * Spanish "amiga" → English "Friend (female)"
    * Spanish "doctora" → English "Doctor (female)"
    * French "actrice" → English "Actress" or "Actor (female)"
    * German "Lehrerin" → English "Teacher (female)"
    * Spanish "buenos días" → English "Good morning"
-   For the distractors in such cases, maintain consistency (if the correct answer has gender clarification, distractors should too when appropriate)

**CRITICAL CONSTRAINT: Distractor Quality & Language Adherence**
-   **Language:** Ensure distractors are ALWAYS in the correct language specified for that field (Source Language for SourceWord.Options, Target Language for Translations[TargetLang].Options).
-   **Distinctness:** All distractors (for both source and translation) MUST be **semantically distinct** from the correct word and from each other. Avoid synonyms, near-synonyms, or slight variations.
-   **Plausibility:** Aim for distractors that test comprehension (e.g., different meaning, same category, opposites). They must be clearly incorrect translations/equivalents of the original source word.
-   **Clarity:** The final list of 4 options (1 correct, 3 distinct distractors) must contain words clearly different from one another.

**Output Format:**
-   Respond ONLY with a valid JSON array.
-   Do NOT include the source language within the "Translations" object.
-   Follow this structure precisely:

[
  {
    "SourceWord": {
      "Correct": "/* The source word in ${sourceLang} */", // Added placeholder for clarity
      "Options": [
        "/* Correct word in ${sourceLang} */",
        "/* Distractor 1 in ${sourceLang} */",
        "/* Distractor 2 in ${sourceLang} */",
        "/* Distractor 3 in ${sourceLang} */"
      ]
    },
    "Translations": {
      // Example for one target language:
      "${targetLangs[0] || "TARGET_LANG_1"}": {
        "Correct": "/* Correct translation in ${targetLangs[0] || "TARGET_LANG_1"} */",
        "Options": [
          "/* Correct translation in ${targetLangs[0] || "TARGET_LANG_1"} */",
          "/* Distractor 1 in ${targetLangs[0] || "TARGET_LANG_1"} */",
          "/* Distractor 2 in ${targetLangs[0] || "TARGET_LANG_1"} */",
          "/* Distractor 3 in ${targetLangs[0] || "TARGET_LANG_1"} */"
        ]
      }
      // ... potentially more target languages following the same pattern
    }
  },
  // ... object for the next source word
]
  `;

  // --- User Prompt (Remains the same, focuses on inputs) ---
  const userPrompt = `
Source Language: ${sourceLang}
Words to process: ${words.join(", ")}
Target Languages for this request: ${targetLangs.join(", ")}

Generate the vocabulary learning JSON based on the system instructions. Pay close attention to:
1. Generating high-quality, semantically distinct distractors in the CORRECT language for all 'Options' fields (Source Language for SourceWord options, Target Language for translation options).
2. Capitalizing ONLY the first letter of each translation using sentence case (e.g., "Good morning", "Thank you", "How are you").
3. Adding plural clarifications (plural) when translating second person plural forms to English (e.g., "ustedes caminan" → "You walk (plural)").
4. Adding gender clarifications (female)/(male) with lowercase gender markers when translating gendered words to languages without gender distinctions (particularly when translating to English).
5. Ensuring the final output is only the valid JSON array.
  `;

  return {
    messages: [
      {
        role: "system",
        content: systemContent,
      },
      {
        role: "user",
        content: userPrompt,
      },
    ],
    temperature: 0.6, // Keep slightly lower temp
    max_tokens: 1000,
  };
}

export function buildImageDescriptionPrompt(sourceLang: string, sourceWord: string) {
  // System prompt sets the stage, constraints, and output requirements.
  const systemContent = `
You are an AI assistant tasked with generating detailed image descriptions. These descriptions will be used as prompts for an image generation model to create visuals for a ${sourceLang} language learning course.

Your goal is to create a description that vividly depicts the subject provided by the user.

The description MUST include the following three exact phrases:
1. "It should be animated yet somewhat realistic."
2. "Make it lively with the subject clearly shown."
3. "easy to identify for ${sourceLang} learners."

Crucially, the description must instruct the image generation model NOT to include the text "${sourceWord}" or any other text labels within the final image itself.

Ensure the final description is engaging, detailed, and focuses on visual elements. Output ONLY the image description text, with no additional commentary, introduction, or explanation.
  `;

  // User prompt provides the specific subject for this request.
  const userPrompt = `
Generate an image description for the subject: "${sourceWord}"
  `;

  return {
    messages: [
      {
        role: "system",
        content: systemContent.trim(),
      },
      {
        role: "user",
        content: userPrompt.trim(),
      },
    ],
    temperature: 0.7, // Keep temperature for creativity in description
    max_tokens: 300, // Keep max_tokens for potentially detailed descriptions
  };
}

/**
 * Generates an initial image prompt payload based on the provided image description.
 * This version returns a payload with a descriptive prompt and additional parameters,
 * where the prompt is directly taken from the string result generated by buildImageDescriptionPrompt.
 *
 * @param imageDescription The image description string from buildImageDescriptionPrompt.
 * @returns The payload for the image generation endpoint.
 */
export function buildBulkEditPrompt(
  editInstructions: string,
  currentVocabulary: {
    targWord: string;
    targOptions: string[];
    targLangCode: string;
    translations: {
      baseWord: string;
      baseOptions: string[];
      baseLangCode: string;
    }[];
  }
) {
  const systemContent = `
You are an AI assistant specialized in editing vocabulary data for language learning applications.
Your task is to apply specific editing instructions to vocabulary entries while maintaining data structure and language consistency.

Current vocabulary data structure:
- Target Word: The main word in the source language
- Target Options: Multiple choice options for the target word
- Translations: Words in other languages with their respective options

IMPORTANT RULES:
1. Maintain the same data structure - do not add or remove languages
2. Keep language codes consistent (ES, EN, FR, DE, IT, PT)
3. Ensure all options remain semantically distinct and in the correct language
4. Apply edits consistently across all translations when relevant
5. Preserve the educational value of the vocabulary

Output Format:
Respond ONLY with a valid JSON object matching this exact structure:
{
  "targWord": "edited word",
  "targOptions": ["option1", "option2", "option3", "option4"],
  "translations": [
    {
      "baseLangCode": "XX",
      "baseWord": "translated word",
      "baseOptions": ["option1", "option2", "option3", "option4"]
    }
  ]
}
`;

  const userPrompt = `
Current vocabulary:
Target Word (${currentVocabulary.targLangCode}): ${currentVocabulary.targWord}
Target Options: ${currentVocabulary.targOptions.join(", ")}

Translations:
${currentVocabulary.translations
  .map((t) => `- ${t.baseLangCode}: ${t.baseWord} (Options: ${t.baseOptions.join(", ")})`)
  .join("\n")}

Edit Instructions:
${editInstructions}

Apply the edits and return the modified vocabulary data in the specified JSON format.
`;

  return {
    messages: [
      {
        role: "system",
        content: systemContent.trim(),
      },
      {
        role: "user",
        content: userPrompt.trim(),
      },
    ],
    temperature: 0.3, // Low temperature for consistent edits
    max_tokens: 1500, // Increased to handle larger vocabularies with multiple translations
    json_mode: true, // Force JSON-only responses
  };
}

export function buildGenAiInitialImagePromptPayload(imageDescription: string) {
  return {
    prompt: imageDescription,
    model: "dall-e-3",
    n: 1,
    size: "1024x1024",
    response_format: "url",
  };
}

/**
 * Generates an image prompt payload based on the provided SourceWord and a user-defined style prompt.
 * This version returns a payload with a descriptive prompt and additional parameters.
 *@param sourceLang
 * @param sourceWord The vocabulary word for which to generate the image.
 * @param stylePrompt The custom style prompt provided by the user.
 * @returns The payload for the image generation endpoint.
 */
export function buildGenAiImagePayload(
  sourceWord: string,
  stylePrompt: string,
  sourceLang: string
) {
  // Combine the vocabulary word with the custom style details.
  const prompt = `A depiction of "${sourceWord}" rendered in a description inspired by: "${stylePrompt}" suitable for a ${sourceLang} language learning course.`;

  return {
    prompt,
    model: "dall-e-3",
    n: 1,
    size: "1024x1024",
    response_format: "url",
  };
}

/**
 * Builds the OpenAI payload for generating multiple random topics
 * in the given language and genre.
 */
export function buildRandomTopicPrompt(
  genre: string,
  section: string,
  count: number,
  language: string
) {
  // This prompt now asks for topics that also relate to the chosen section.
  const userPrompt = `
    Generate exactly ${count} random topics or themes suitable for a story in the genre of "${genre}" that relate to the section "${section}".
    Each topic must be in ${language}.
    Do not include any additional text, explanations, or commentary beyond the topics themselves.
    Return the result as a valid JSON array of exactly ${count} strings, e.g., ["Topic 1", "Topic 2", "Topic 3"] if ${count} is 3.
    Ensure the output contains precisely ${count} items—no more, no less.
  `;

  return {
    messages: [
      {
        role: "user",
        content: userPrompt,
      },
    ],
    temperature: 0.7,
    max_tokens: 200,
  };
}

/**
 * Builds the OpenAI payload for generating a passage,
 * given Genre, Topic, ProficiencyLevel, and the relevant vocabularyList.
 */
export function buildPassagePrompt(
  genre: string,
  topic: string,
  section: string,
  proficiencyLevel: string,
  vocabularyList: string[],
  language: string
) {
  // Level descriptions matching the script's system
  const levelDescriptions: { [key: string]: string } = {
    "1": "user that is incredibly new to the language and only can understand a few greetings. Can only understand present tense.",
    "2": "user that is incredibly new to the language and only can understand a few greetings and very basic words. Can only understand present tense.",
    "3": "user that is very new to the language and is learning basic verbs, questions, ser and estar. Can only understand present tense and is learning the words for locations, food, transportation, colors and adjectives. They have not learned past or future tense.",
    "4": "user that is new to the language and is learning present progressive and present tense. They only understand basic verbs, adjectives and nouns. They are learning the words for locations, ir, periphrastic future, tener. They have not learned past or future tense.",
    "5": `user that is new to the language and is learning emotions, common items, expressing opinions. These are the topics they are learning: Emotions, Common Items, To be able to, Opinions, Better & Worse, Thinking, Weather & Nature, Household items & Furniture, Entertainment & Free time, Some more verbs, and these are the ones they have learned: Greetings, Useful Phrases, Useful Phrases: Part 2, Masculine vs Feminine, Plural vs Singular, Pronombres, Verbos principales, Verbos Regulares, Regular Verbs, Interrogatives, To be (Permanent), To be (Temporary), Present Progressive, Transportation, Colors, Adjectives, Possessive Pronouns, Possessive Adjective, To want, Food & Drink 1, To want Part two, Places, To go, Going to do, To Have, Tener + verb, Numbers 1-10, Numbers 11-100, Clothing.`,
    "6": `user that is new to the language that is learning these sections: To know, Clothes, Travel, Commands & Requests, Body parts, Hobbies, Technology, Months and have learned these sections: Greetings, Useful Phrases, Useful Phrases: Part 2, Masculine vs Feminine, Plural vs Singular, Pronombres, Verbos principales, Verbos Regulares, Regular Verbs, Interrogatives, To be (Permanent), To be (Temporary), Present Progressive, Transportation, Colors, Adjectives, Possessive Pronouns, Possessive Adjective, To want, Food & Drink 1, To want Part two, Places, To go, Going to do, To Have, Tener + verb, Numbers 1-10, Numbers 11-100, Clothing, Emotions, Common Items, To be able to, Opinions, Better & Worse, Thinking, Weather & Nature, Household items & Furniture, Entertainment & Free time, Some more verbs.`,
    "7": `A user that is a novice to the language that is learning these sections: Past tense - Preterite, More Preterite, Some More Preterite, Even More Preterite, Conditional, Story telling, Past tense - Imperfect, More Imperfect, Some More Imperfect, Even More Imperfect and have learned these sections: Greetings, Useful Phrases, Useful Phrases: Part 2, Masculine vs Feminine, Plural vs Singular, Pronombres, Verbos principales, Verbos Regulares, Regular Verbs, Interrogatives, To be (Permanent), To be (Temporary), Present Progressive, Transportation, Colors, Adjectives, Possessive Pronouns, Possessive Adjective, To want, Food & Drink 1, To want Part two, Places, To go, Going to do, To Have, Tener + verb, Numbers 1-10, Numbers 11-100, Clothing, Emotions, Common Items, To be able to, Opinions, Better & Worse, Thinking, Weather & Nature, Household items & Furniture, Entertainment & Free time, Some more verbs.`,
    "8": `A user that is a novice to the language that is learning these sections: Cooking & Kitchen, Holidays and Celebrations, Formal, Professions, Reflexive verbs, Shopping & Money, School, Daily Routine, Verbs… Again. And has completed these sections: Greetings, Useful Phrases, Useful Phrases: Part 2, Masculine vs Feminine, Plural vs Singular, Pronombres, Verbos principales, Verbos Regulares, Regular Verbs, Interrogatives, To be (Permanent), To be (Temporary), Present Progressive, Transportation, Colors, Adjectives, Possessive Pronouns, Possessive Adjective, To want, Food & Drink 1, To want Part two, Places, To go, Going to do, To Have, Tener + verb, Numbers 1-10, Numbers 11-100, Clothing, Emotions, Common Items, To be able to, Opinions, Better & Worse, Thinking, Weather & Nature, Household items & Furniture, Entertainment & Free time, Some more verbs, To know, Clothes, Travel, Commands & Requests, Body parts, Hobbies, Technology, Months.`,
    "9": `A user that is intermediate in the language that is learning these sections: Conditional mood - I would like, Body parts, Physical descriptions, Sports and fitness, Por vs para, More foods, Verbs pt.4, Nature, Society, Future tense, Future tense practice, More future practice and has completed these sections: Greetings, Useful Phrases, Useful Phrases: Part 2, Masculine vs Feminine, Plural vs Singular, Pronombres, Verbos principales, Verbos Regulares, Regular Verbs, Interrogatives, To be (Permanent), To be (Temporary), Present Progressive, Transportation, Colors, Adjectives, Possessive Pronouns, Possessive Adjective, To want, Food & Drink 1, To want Part two, Places, To go, Going to do, To Have, Tener + verb, Numbers 1-10, Numbers 11-100, Clothing, Emotions, Common Items, To be able to, Opinions, Better & Worse, Thinking, Weather & Nature, Household items & Furniture, Entertainment & Free time, Some more verbs, To know, Clothes, Travel, Commands & Requests, Body parts, Hobbies, Technology, Months, Past tense - Preterite, More Preterite, Some More Preterite, Even More Preterite, Conditional, Story telling, Past tense - Imperfect, More Imperfect, Some More Imperfect, Even More Imperfect, Cooking & Kitchen, Holidays and Celebrations, Formal, Professions, Reflexive verbs, Shopping & Money, School, Daily Routine, Verbs… Again.`,
    "10": "A user at a B2 language level.",
  };

  // Bucket raw proficiency level (1-100) into descriptive levels (1-10)
  let bucket: string;
  try {
    const lvl = parseInt(proficiencyLevel);
    if (lvl <= 9) bucket = "1";
    else if (lvl <= 15) bucket = "2";
    else if (lvl <= 25) bucket = "3";
    else if (lvl <= 36) bucket = "4";
    else if (lvl <= 44) bucket = "5";
    else if (lvl <= 54) bucket = "6";
    else if (lvl <= 65) bucket = "7";
    else if (lvl <= 76) bucket = "8";
    else if (lvl <= 85) bucket = "9";
    else bucket = "10";
  } catch {
    bucket = "1";
  }

  const levelDescription = levelDescriptions[bucket] || "Beginner";

  // Language-specific custom directions
  const languageCustomDirections: { [key: string]: string } = {
    Portuguese:
      "Use only Brazilian Portuguese (português brasileiro) with Brazilian vocabulary, expressions, and cultural references.",
    Spanish:
      "Use Latin American Spanish with neutral vocabulary that is widely understood across Spanish-speaking countries.",
    French: "Use standard French (français standard) with contemporary vocabulary and expressions.",
    German: "Use standard German (Hochdeutsch) with modern vocabulary and expressions.",
    Italian: "Use standard Italian with contemporary vocabulary and expressions.",
    English: "Use clear, standard English with contemporary vocabulary and expressions.",
  };

  const customDirections = languageCustomDirections[language] || "";
  const vocabularyText = vocabularyList.join(", ");
  const customDirectionsText = customDirections
    ? `\n\nIMPORTANT LANGUAGE REQUIREMENT: ${customDirections}`
    : "";

  const userPrompt = `Please generate a creative and engaging passage about "${topic}" that is centered around the section "${section}".
The passage may take the form of a narrative story or a dialogue between characters—whichever suits the content best.
It should be coherent and suitable for ${levelDescription} ${language} learners, and include only ${language} text.
While you should emphasize incorporating the following vocabulary if they naturally fit, do not force their inclusion if it compromises the flow: ${vocabularyText}.${customDirectionsText}

Generate exactly four questions about the passage. For each question, provide:
1. The question text.
2. Exactly four multiple-choice options, clearly labeling one as the correct answer.
3. Identify the correct answer among the options.

Ensure that the JSON response strictly follows this structure:

{
  "Targ_Lang_Title": "Passage Title",
  "Targ_Lang_Description": "Short description of the passage",
  "Targ_Lang_Passage": "The full text of the passage. Use \\n to indicate line breaks between paragraphs.",
  "Targ_Lang_Question_1": "First question about the passage",
  "Targ_Lang_Answer_1": "The correct answer to Question 1",
  "Targ_Lang_Options_1": [
    "Option A",
    "Option B",
    "Option C",
    "Option D"
  ],
  "Targ_Lang_Question_2": "Second question about the passage",
  "Targ_Lang_Answer_2": "The correct answer to Question 2",
  "Targ_Lang_Options_2": [
    "Option A",
    "Option B",
    "Option C",
    "Option D"
  ],
  "Targ_Lang_Question_3": "Third question about the passage",
  "Targ_Lang_Answer_3": "The correct answer to Question 3",
  "Targ_Lang_Options_3": [
    "Option A",
    "Option B",
    "Option C",
    "Option D"
  ],
  "Targ_Lang_Question_4": "Fourth question about the passage",
  "Targ_Lang_Answer_4": "The correct answer to Question 4",
  "Targ_Lang_Options_4": [
    "Option A",
    "Option B",
    "Option C",
    "Option D"
  ]
}

**Important Instructions:**
- **Use \\n** to indicate line breaks between paragraphs in the "Passage" field.
- **Exactly four options** must be provided for each question.
- One of the options must **explicitly be the correct answer**.
- The JSON response should **strictly adhere** to the provided structure with no additional text or commentary.
- ****All text returned should be in ${language}******.
- **Output only** the JSON object and nothing else.
`;

  return {
    messages: [
      {
        role: "user",
        content: userPrompt,
      },
    ],
    temperature: 0.7,
    max_tokens: 2000,
  };
}

/**
 * Builds the payload for initial image generation based on a passage.
 * The prompt is constructed using the passage text and default style instructions.
 *
 * @param passage The generated passage text.
 * @param language The target language (used in the style instructions).
 * @returns The payload for the image generation endpoint.
 */
export function buildPassageInitialImagePromptPayload(passage: string, language: string) {
  const styleInstructions = `Do not include any text, letters, numbers, or writing in the image. It should be animated yet somewhat realistic. Make it lively with the subject clearly shown. Easy to identify for ${language} learners. The image should be lively, engaging, and detailed. THE IMAGE SHOULD NOT CONTAIN ANY TEXT OR NUMBERS.`;

  const prompt = `THE IMAGE SHOULD NOT CONTAIN ANY TEXT OR NUMBERS. Create an image based on the following passage: "${passage}". ${styleInstructions} Do not include any text, letters, numbers, or writing in the image.`;

  return {
    prompt,
    model: "dall-e-3",
    n: 1,
    size: "1024x1024",
    response_format: "url",
  };
}

/**
 * Builds the payload for custom image generation based on a passage,
 * using a user-provided style prompt.
 *
 * @param passage The generated passage text.
 * @param stylePrompt The custom style prompt provided by the user.
 * @param language The target language.
 * @returns The payload for the image generation endpoint.
 */
export function buildPassageCustomImagePayload(
  passage: string,
  stylePrompt: string,
  language: string
) {
  const prompt = `THE IMAGE SHOULD NOT CONTAIN ANY TEXT OR NUMBERS. Create an image based on the following passage: "${passage}". ${stylePrompt} Make sure it is clear and engaging for ${language} language learners. Do not include any text, letters, numbers, or writing in the image. THE IMAGE SHOULD NOT CONTAIN ANY TEXT OR NUMBERS.`;

  return {
    prompt,
    model: "dall-e-3",
    n: 1,
    size: "1024x1024",
    response_format: "url",
  };
}

// GenAIPrompts.tsx
// In GenAIPrompts/GenAIPrompts.tsx

export function buildTriviaInitialImagePayload(
  topic: string,
  question: string,
  language: string,
  styleInstructions: string // new parameter
) {
  const prompt = `Create an image based on the following topic: "${topic}" and question: "${question}". ${styleInstructions}`;
  return {
    prompt,
    model: "dall-e-3",
    n: 1,
    size: "1024x1024",
    response_format: "url",
  };
}

export function buildTriviaCustomImagePayload(
  topic: string,
  question: string,
  customPrompt: string,
  language: string,
  styleInstructions: string // new parameter
) {
  const prompt = `Create an image based on the following topic: "${topic}" and question: "${question}". ${customPrompt} ${styleInstructions} Make sure it is clear and engaging for ${language} language learners.`;
  return {
    prompt,
    model: "dall-e-3",
    n: 1,
    size: "1024x1024",
    response_format: "url",
  };
}

export function buildGenAiTriviaEnglishPayload(
  difficultyLevel: string,
  genre: string,
  topic: string
) {
  const systemContent = `
You are an assistant that generates multiple-choice trivia questions in English.
Generate exactly 5 questions for the given parameters.
Each question must have exactly 4 answer choices (one correct and 3 plausible distractors).
Return only valid JSON with no extra text, commentary, or code fences. Do not wrap the JSON in markdown formatting.

{
  "topic": "...",
  "questions": [
    { "question": "...", "options": ["...", "...", "...", "..."], "answer": "..." },
    ...
  ]
}
  `.trim();

  const userPrompt = `
Using the following parameters, generate trivia questions in English:
- CEFR Level: ${difficultyLevel} (A1 to C2)
- Genre: ${genre}
- Topic: ${topic}

Generate 5 multiple-choice trivia questions.
Each question must have exactly 4 options, and the correct answer must be one of the options.

Return only valid JSON in the format specified above.
  `.trim();

  return {
    messages: [
      { role: "system", content: systemContent },
      { role: "user", content: userPrompt },
    ],
    max_tokens: 1500,
    temperature: 0.7,
  };
}

// Generates a payload to translate a given set of English questions into a target language.
export function buildGenAiTriviaTranslationPayload(
  difficultyLevel: string,
  genre: string,
  topic: string,
  targetLanguage: string,
  englishQuestions: any[]
) {
  const systemContent = `
You are an assistant that translates multiple-choice trivia questions.
You will receive a set of trivia questions in English.
Translate these questions into ${targetLanguage}, preserving the question text, options, and correct answer.
Return only valid JSON with no extra text, commentary, or code fences. Do not wrap the JSON in markdown formatting.

{
  "language": "${targetLanguage}",
  "questions": [
    { "question": "...", "options": ["...", "...", "...", "..."], "answer": "..." },
    ...
  ]
}
  `.trim();

  // We pass the English questions as context in the user prompt.
  const userPrompt = `
Using the following parameters, translate these trivia questions into ${targetLanguage}:
- CEFR Level: ${difficultyLevel} (A1 to C2)
- Genre: ${genre}
- Topic: ${topic}

English questions:
${JSON.stringify(englishQuestions, null, 2)}

Return only valid JSON in the format specified above.
  `.trim();

  return {
    messages: [
      { role: "system", content: systemContent },
      { role: "user", content: userPrompt },
    ],
    max_tokens: 1000,
    temperature: 0.7,
  };
}

// 1. Build the single-topic prompt
export function buildGenAiTopicPayload(difficultyLevel: string, genre: string) {
  const systemContent = `
You are an assistant that generates trivia topics based on given parameters.
Ensure that your output is strictly valid JSON with no additional commentary.
The JSON must have a single key "topic" whose value is a string representing a trivia subtopic.
  `.trim();

  const userPrompt = `
Generate exactly ONE subtopic for trivia given these parameters:
- CEFR Level: ${difficultyLevel}
- Genre: ${genre}

Return the output as valid JSON, for example:
{
  "topic": "Your subtopic here"
}
  `.trim();

  return {
    messages: [
      {
        role: "system",
        content: systemContent,
      },
      {
        role: "user",
        content: userPrompt,
      },
    ],
    max_tokens: 500,
    temperature: 0.7,
  };
}

/**
 * Generates the payload for creating a single language-learning sentence from a list of vocabulary words.
 *
 * @param vocabularyWords - An array of vocabulary words to incorporate.
 * @param sourceLangName - The name of the language for the sentence (e.g., "English", "Spanish").
 * @param sourceLangCode - The code of the language for the sentence (e.g., "EN", "ES").
 * @param proficiencyLevel - The CEFR proficiency level (e.g., "A1", "B2").
 * @param difficulty - A difficulty rating string (e.g., "1", "3").
 * @param domain - The domain of the content (e.g., "general", "food").
 * @param sectionContext - Context from the selected section (e.g., "Common Phrases").
 * @returns A payload object for the API call.
 */
/**
 * Generates the payload for creating a single language-learning sentence from a list of vocabulary words.
 *
 * @param vocabularyWords - An array of vocabulary words to incorporate.
 * @param sourceLangName - The name of the language for the sentence (e.g., "English", "Spanish").
 * @param sourceLangCode - The code of the language for the sentence (e.g., "EN", "ES").
 * @param proficiencyLevel - The CEFR proficiency level (e.g., "A1", "B2").
 * @param difficulty - A difficulty rating string (e.g., "1", "3").
 * @param domain - The domain of the content (e.g., "general", "food").
 * @param sectionContextDisplay - A string representing the context from the selected section(s) (e.g., "Common Phrases, Greetings").
 * @returns A payload object for the API call.
 */
export function buildSentenceFromVocabListPayload(
  vocabularyWords: string[],
  sourceLangName: string,
  sourceLangCode: string,
  proficiencyLevel: string,
  difficulty: string,
  domain: string,
  sectionContextDisplay: string // Changed from sectionContext
) {
  const systemContent = `
You are an expert language curriculum developer.
Your task is to generate ONE single, grammatically correct, and natural-sounding example sentence in ${sourceLangName} (${sourceLangCode}).
This sentence should be suitable for a learner at the ${proficiencyLevel} proficiency level, with a difficulty rating of ${difficulty}/3.
The sentence should be relevant to the domain of "${domain}" and the general theme of the provided vocabulary words, which come from section(s): "${sectionContextDisplay}".

The sentence MUST incorporate AT LEAST ONE, and ideally several, of the following ${sourceLangName} vocabulary words:
${vocabularyWords.map((word) => `- ${word}`).join("\n")}

If not all words can be naturally incorporated, prioritize the most relevant ones for the context and proficiency.
The sentence should be concise yet illustrative of the vocabulary in context.

For the "correctWords" array, list meaningful phrases or chunks and punctuation marks from the generated sentence, in order.
These phrases/chunks should not separate closely bound grammatical units (like pronoun-verb, article-noun pairs if they form a single idea for the exercise).
Refer to these examples for "correctWords" structure:
1. If the sentence is "Could you please help me with my homework?", "correctWords" might be: ["Could you", "please", "help me", "with my", "homework", "?"].
2. If the sentence is "I would like a glass of water, please.", "correctWords" might be: ["I would like", "a glass of", "water", ",", "please", "."].
3. If the sentence is "The children are playing in the park.", "correctWords" might be: ["The children", "are playing", "in", "the park", "."].

Generate only between 2 to 4 additional "distractorWords" in ${sourceLangName}.
These "distractorWords" should be:
- Similar in style or content to the words in the sentence.
- Grammatically plausible if inserted in a similar context, but incorrect for THIS specific sentence.
- Single words or very short phrases.
- WITHOUT any punctuation marks.
- CRITICALLY: "distractorWords" MUST NOT contain any of the exact words or phrases present in the "correctWords" array you generate for this sentence. They must be entirely distinct.

Return ONLY a single valid JSON object with no extra text, commentary, or markdown formatting.
Follow this exact JSON format:
{
  "sentence": "Full sentence text in ${sourceLangName}.",
  "correctWords": ["Phrase1_or_Word1", "Punctuation", "Phrase2_or_Word2", "..."],
  "distractorWords": ["Distractor1", "Distractor2", ...]
}
  `.trim();

  const userPrompt = `
Generate one language-learning sentence using the following parameters:
- Language: ${sourceLangName} (${sourceLangCode})
- Vocabulary Words to use: ${vocabularyWords.join(", ")}
- Proficiency Level: ${proficiencyLevel}
- Difficulty: ${difficulty}
- Domain: ${domain}
- Section Context(s): ${sectionContextDisplay}

Remember to structure "correctWords" as meaningful phrases/chunks and punctuation.
Provide 2-4 "distractorWords" that are distinct from the "correctWords".
Return the output strictly in the single JSON object format specified.
  `.trim();

  return {
    messages: [
      { role: "system", content: systemContent },
      { role: "user", content: userPrompt },
    ],
    max_tokens: 1000,
    temperature: 0.7,
  };
}

/**
 * Generates the payload for translating language-learning content (a single sentence object)
 * into a specified target language.
 *
 * @param originalSentenceDataObjectJSON - The JSON string of the single SentenceOutputData object to translate.
 * @param sourceLangName - The name of the language of the originalSentenceDataObjectJSON (e.g., "English", "Spanish").
 * @param targetLangName - The name of the language to translate into (e.g., "Spanish", "German").
 * @param exampleTargetSentence - An example sentence in the target language to guide the AI for structure.
 * @param exampleTargetCorrectWords - An example of "correctWords" in the target language.
 * @returns A payload object for the translation API call.
 */
function buildGenericTranslationPayload(
  originalSentenceDataObjectJSON: string,
  sourceLangName: string,
  targetLangName: string,
  exampleTargetSentence: string,
  exampleTargetCorrectWords: string[]
) {
  const systemContent = `
You are an expert assistant that translates language-learning content.
The input is a JSON object string containing a sentence, its "correctWords" (meaningful phrases/chunks and punctuation), and "distractorWords".
The input sentence and its components are in ${sourceLangName}.
Your task is to translate this entire content into ${targetLangName}.

Ensure that:
- You translate the main "sentence".
- You translate each element in the "correctWords" array. The translated "correctWords" should maintain the same phrasal/chunk structure and include all punctuation as in the source, adapted for ${targetLangName}.
- You translate each element in the "distractorWords" array. These translated distractors should remain plausible alternatives in ${targetLangName}. Generate 2-4 such distractors.
- CRITICALLY: The translated "distractorWords" MUST NOT contain any of the exact words or phrases present in the translated "correctWords" array you generate for this sentence. They must be entirely distinct.

For example, if translating into ${targetLangName} and the source implies a sentence like "${exampleTargetSentence}",
the translated "correctWords" array should be structured like: ${JSON.stringify(
    exampleTargetCorrectWords
  )}.

Return ONLY a single valid JSON object in ${targetLangName} with no additional text or formatting.
Follow this exact JSON format:
{
  "sentence": "Translated sentence text in ${targetLangName}.",
  "correctWords": ["Translated_Phrase1", "Translated_Punctuation", "Translated_Phrase2", "..."],
  "distractorWords": ["Translated_Distractor1", "Translated_Distractor2", ...]
}
  `.trim();

  const userPrompt = `
Translate the following language-learning content from ${sourceLangName} into ${targetLangName}.
The content is a single JSON object. Preserve the phrasal structure for "correctWords".
Ensure the translated "distractorWords" are distinct from the translated "correctWords".

Original Content (in ${sourceLangName}):
${originalSentenceDataObjectJSON}

Return the translated content strictly in the single JSON object format specified.
  `.trim();

  return {
    messages: [
      { role: "system", content: systemContent },
      { role: "user", content: userPrompt },
    ],
    max_tokens: 3000,
    temperature: 0.7,
  };
}

// --- Specific Language Translation Payload Builders (no change needed here, they use the generic one) ---

export function buildSpanishTranslationPayload(
  originalSentenceDataObjectJSON: string,
  sourceLangName: string
) {
  return buildGenericTranslationPayload(
    originalSentenceDataObjectJSON,
    sourceLangName,
    "Spanish",
    "¿Podrías por favor ayudarme con mi tarea?",
    ["¿Podrías", "por favor", "ayudarme", "con mi", "tarea?"]
  );
}

export function buildGermanTranslationPayload(
  originalSentenceDataObjectJSON: string,
  sourceLangName: string
) {
  return buildGenericTranslationPayload(
    originalSentenceDataObjectJSON,
    sourceLangName,
    "German",
    "Könntest du mir bitte mit meinen Hausaufgaben helfen?",
    ["Könntest du", "mir bitte", "mit meinen", "Hausaufgaben", "helfen?"]
  );
}

export function buildItalianTranslationPayload(
  originalSentenceDataObjectJSON: string,
  sourceLangName: string
) {
  return buildGenericTranslationPayload(
    originalSentenceDataObjectJSON,
    sourceLangName,
    "Italian",
    "Potresti per favore aiutarmi con i miei compiti?",
    ["Potresti", "per favore", "aiutarmi", "con i miei", "compiti?"]
  );
}

export function buildFrenchTranslationPayload(
  originalSentenceDataObjectJSON: string,
  sourceLangName: string
) {
  return buildGenericTranslationPayload(
    originalSentenceDataObjectJSON,
    sourceLangName,
    "French",
    "Pourriez-vous s'il vous plaît m'aider avec mes devoirs?",
    ["Pourriez-vous", "s'il vous plaît", "m'aider", "avec mes", "devoirs?"]
  );
}

export function buildPortugueseTranslationPayload(
  originalSentenceDataObjectJSON: string,
  sourceLangName: string
) {
  return buildGenericTranslationPayload(
    originalSentenceDataObjectJSON,
    sourceLangName,
    "Portuguese",
    "Você poderia, por favor, me ajudar com minha lição de casa?",
    ["Você poderia", "por favor", "me ajudar", "com minha", "lição de casa?"]
  );
}

export function buildEnglishTranslationPayload(
  originalSentenceDataObjectJSON: string,
  sourceLangName: string
) {
  return buildGenericTranslationPayload(
    originalSentenceDataObjectJSON,
    sourceLangName,
    "English",
    "Could you please help me with my homework?",
    ["Could you", "please", "help me", "with my", "homework", "?"]
  );
}

/**
 * Builds the payload for a Generative AI request to create fill-in-the-blank
 * vocabulary/grammar exercises in JSON format.
 *
 * @param sourceLang - The language code (e.g., "EN", "ES") of the sentences and options.
 * @param vocabularyInputs - An array of objects, each containing the 'correctWord' to build a sentence around.
 * @param availableLanguagesList - An array of strings representing all available languages.
 * @param proficiencyLevel - The target proficiency level (e.g., "A1", "B2").
 * @param difficulty - The target difficulty (e.g., "1", "2", "3").
 * @param domain - The domain of the vocabulary (e.g., "general", "medical").
 * @returns The structured payload for the AI request.
 */
export function buildFillInTheBlankGenAiPayload(
  sourceLang: string,
  vocabularyInputs: { sentence: string; correctWord: string }[], // 'sentence' is placeholder context
  availableLanguagesList: string[],
  proficiencyLevel: string,
  difficulty: string,
  domain: string
) {
  const systemContent = `
You are an AI assistant specialized in creating JSON data for "fill-in-the-blank" language learning exercises.
Your primary task is to take a list of **Correct Words** (vocabulary items) provided in a specific **Source Language**.
The target audience has a proficiency level of **${proficiencyLevel}** in the **Source Language** (${sourceLang}), and the desired difficulty for the exercises is **${difficulty} (out of 3, where 1 is easiest)**. The domain for these words is **${domain}**.

For each **Correct Word**, you must:

1.  **Generate a Sentence:**
    * Create a meaningful and contextually appropriate sentence in the **Source Language** (${sourceLang}) that naturally uses the provided **Correct Word**.
    * This sentence MUST be suitable for a learner at the **${proficiencyLevel}** proficiency level and match the **${difficulty}** difficulty.
    * The sentence should be relevant to the **${domain}** domain if applicable, or general if the domain is "general".
    * Ensure the **Correct Word** fits grammatically and semantically into this sentence.

2.  **Create a Blank:**
    * In the sentence you just generated, replace the **Correct Word** with a single, consistent blank placeholder (e.g., "___"). This modified sentence will be the "sentence" field in your JSON output.

3.  **Identify Correct Answer:**
    * The original **Correct Word** is the "correctAnswer" for the exercise.

4.  **Generate Distractors:**
    * Create exactly 3 **incorrect** but plausible distractors for the blank in your generated sentence. These distractors should also be appropriate for the **${proficiencyLevel}** level and **${difficulty}**.
    * **Language Constraint:** Distractors MUST be valid words or short phrases exclusively in the **Source Language** (${sourceLang}).
    * **Plausibility & Grammar:** Distractors should fit grammatically but make the sentence semantically incorrect.
    * **Distinctness:** Distractors must be clearly different from the "correctAnswer" and each other.

5.  **Format Options Array:**
    * Create an "options" array containing: the "correctAnswer" and your three distractors.

**Context for AI:**
* This data is for a language learning application supporting multiple languages. The full list of potentially available languages includes: ${availableLanguagesList.join(
    ", "
  )}.
* All generated content (sentences, distractors) MUST strictly adhere to the provided **Source Language** (${sourceLang}).

**Output Format Requirements:**
-   Respond ONLY with a valid JSON array, where each object represents one fill-in-the-blank question.
-   Do NOT include any introductory text, explanations, apologies, or markdown formatting.
-   The structure for each generated question must be:
[
  {
    "sentence": "/* The NEWLY GENERATED sentence with '___' placeholder in ${sourceLang} for proficiency ${proficiencyLevel}, difficulty ${difficulty}, domain ${domain} */",
    "correctAnswer": "/* The original Correct Word provided in the input, in ${sourceLang} */",
    "options": [
      "/* The correctAnswer (original Correct Word) */",
      "/* Distractor 1 in ${sourceLang} (suitable for ${proficiencyLevel}, difficulty ${difficulty}) */",
      "/* Distractor 2 in ${sourceLang} (suitable for ${proficiencyLevel}, difficulty ${difficulty}) */",
      "/* Distractor 3 in ${sourceLang} (suitable for ${proficiencyLevel}, difficulty ${difficulty}) */"
    ]
  }
  // ... more objects
]
`;

  const formattedInputsForAI = vocabularyInputs
    .map(
      (input, index) =>
        `${index + 1}. Process this **Correct Word**: "${
          input.correctWord
        }" (Original context: "${input.sentence}")`
    )
    .join("\n\n");

  const userPrompt = `
Source Language for this request: ${sourceLang}
Target Proficiency Level: ${proficiencyLevel}
Target Difficulty (1-3): ${difficulty}
Domain: ${domain}

Based on the detailed system instructions, generate fill-in-the-blank JSON data for each of the following **Correct Words**.
For each **Correct Word**:
1. Create a new, contextually relevant sentence in ${sourceLang} with a "___" blank where the **Correct Word** would fit, suitable for the specified proficiency, difficulty, and domain.
2. Provide the **Correct Word** as the correctAnswer.
3. Generate 3 distinct distractors in ${sourceLang}, also suitable for the proficiency and difficulty.

**Correct Words to Process:**
${formattedInputsForAI}

Ensure the final output is ONLY the valid JSON array.
`;

  return {
    messages: [
      { role: "system", content: systemContent },
      { role: "user", content: userPrompt },
    ],
    temperature: 0.7,
    max_tokens: 3072, // Increased slightly for potentially more complex sentences/prompts
  };
}

/**
 * Generates word description exercises with multiple choice options and hints.
 * Creates exercises where learners read hints in base language and choose correct target language word.
 *
 * @param words - Array of vocabulary words to generate exercises for
 * @param baseLanguage - Language for descriptions/hints (e.g., "English")
 * @param targetLanguage - Language for answer options (e.g., "Spanish")
 * @param domain - Domain context (e.g., "general", "medical", "construction")
 * @param proficiencyLevel - Target proficiency level (A1-C2)
 * @param difficulty - Difficulty level (1-3)
 * @returns Payload for OpenAI API call
 */
export function buildWordDescriptionPrompt(
  words: string[],
  baseLanguage: string,
  targetLanguage: string,
  domain: string,
  proficiencyLevel: string,
  difficulty: string
) {
  const systemContent = `
You are an AI assistant specialized in creating multiple choice word description exercises for language learning.

Your task is to generate exercises where learners:
1. Read hints/descriptions in ${baseLanguage}
2. Choose the correct word from multiple choice options in ${targetLanguage}

For each vocabulary word provided, create an exercise with:
- 4 multiple choice options in ${targetLanguage} (1 correct + 3 distractors)
- 2-3 helpful hints with both ${baseLanguage} and ${targetLanguage} versions
- Appropriate difficulty for ${proficiencyLevel} level learners

Guidelines:
- Difficulty ${difficulty}: 1=easy, 2=medium, 3=hard
- Domain context: "${domain}"
- Hints should be clear but not give away the answer directly
- CRITICAL: Never use the target word itself or direct translations in any hints
- CRITICAL: Avoid using any of the multiple choice options in the hint text
- Distractors should be plausible but clearly wrong
- Use vocabulary appropriate for the proficiency level

Return ONLY a valid JSON array with this exact structure:
[
  {
    "correctOption": "word_in_${targetLanguage}",
    "options": ["correct_word", "distractor1", "distractor2", "distractor3"],
    "hints": [
      {
        "baseText": "Helpful hint in ${baseLanguage}",
        "targetText": "Translation of hint in ${targetLanguage}"
      },
      {
        "baseText": "Another hint in ${baseLanguage}",
        "targetText": "Translation of hint in ${targetLanguage}"
      }
    ]
  }
]

Example format for Spanish learning with English hints:
[
  {
    "correctOption": "manzana",
    "options": ["manzana", "plátano", "naranja", "uva"],
    "hints": [
      {
        "baseText": "It's a red or green fruit that grows on trees.",
        "targetText": "Es una fruta roja o verde que crece en los árboles."
      },
      {
        "baseText": "It keeps the doctor away when eaten daily.",
        "targetText": "Mantiene al médico alejado cuando se come diariamente."
      },
      {
        "baseText": "It's often used to make juice, pies, and sauce.",
        "targetText": "Se usa a menudo para hacer jugo, pasteles y salsa."
      }
    ]
  }
]
  `.trim();

  const userPrompt = `
Generate word description exercises for the following parameters:
- Base Language (for hints): ${baseLanguage}
- Target Language (for options): ${targetLanguage}
- Domain: ${domain}
- Proficiency Level: ${proficiencyLevel}
- Difficulty: ${difficulty}

Words to create exercises for: ${words.join(", ")}

Create multiple choice exercises with helpful hints in both languages for each word.
Return only the JSON array as specified in the system instructions.
  `.trim();

  return {
    messages: [
      { role: "system", content: systemContent },
      { role: "user", content: userPrompt },
    ],
    temperature: 0.7,
    max_tokens: 3000,
  };
}

/**
 * Build a GenAI prompt to generate a Translation Mediator conversation.
 * Two avatars who don't speak the same language have a conversation,
 * and the user acts as interpreter translating between them.
 */
export function buildTranslationMediatorPrompt(
  vocabularyWords: string[],
  targetLanguage: string,
  targetLanguageCode: string,
  baseLanguage: string,
  baseLanguageCode: string,
  proficiencyLevel: string,
  difficulty: string,
  sectionContext: string,
  numTurns: number = 4
) {
  const vocabList = vocabularyWords.map((w, i) => `${i + 1}. ${w}`).join("\n");

  const systemContent = `
You are a language exercise generator. You create Translation Mediator exercises
where two people who don't speak the same language have a conversation,
and a language learner acts as interpreter.

RULES:
1. Avatar A speaks ONLY ${baseLanguage} (${baseLanguageCode})
2. Avatar B speaks ONLY ${targetLanguage} (${targetLanguageCode})
3. The conversation alternates: A speaks, then B responds, then A, then B, etc.
4. Each turn includes the original text and the expected translation to the OTHER language
5. Incorporate vocabulary words naturally where possible
6. Keep sentences appropriate for ${proficiencyLevel} level, difficulty ${difficulty}/3
7. Make the conversation feel natural and realistic
8. Pick a different gender for each avatar (one male, one female)

OUTPUT FORMAT - respond ONLY with a valid JSON object:
{
  "conversationTopic": "Brief topic (e.g., 'Ordering coffee at a cafe')",
  "avatarAName": "A ${baseLanguage}-appropriate first name",
  "avatarBName": "A ${targetLanguage}-appropriate first name",
  "avatarAGender": "male or female",
  "avatarBGender": "the opposite gender",
  "turns": [
    {
      "turnIndex": 0,
      "speaker": "A",
      "originalText": "What Avatar A says in ${baseLanguage}",
      "expectedTranslation": "Correct translation into ${targetLanguage}",
      "hintText": "Same as expectedTranslation"
    },
    {
      "turnIndex": 1,
      "speaker": "B",
      "originalText": "What Avatar B says in ${targetLanguage}",
      "expectedTranslation": "Correct translation into ${baseLanguage}",
      "hintText": "Same as expectedTranslation"
    }
  ]
}

IMPORTANT:
- Avatar A lines: originalText in ${baseLanguage}, expectedTranslation in ${targetLanguage}
- Avatar B lines: originalText in ${targetLanguage}, expectedTranslation in ${baseLanguage}
- Generate exactly ${numTurns} turns (alternating A, B, A, B...)
`.trim();

  const userPrompt = `
Section context: "${sectionContext}"
Target Language: ${targetLanguage} (${targetLanguageCode})
Base Language: ${baseLanguage} (${baseLanguageCode})
Proficiency Level: ${proficiencyLevel}
Difficulty: ${difficulty}/3

Vocabulary words to incorporate (in ${targetLanguage}):
${vocabList}

Generate a natural ${numTurns}-turn conversation exercise.
`.trim();

  return {
    messages: [
      { role: "system", content: systemContent },
      { role: "user", content: userPrompt },
    ],
    temperature: 0.5,
    max_tokens: 4000,
    json_mode: true,
  };
}
